import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActivityFeed from './ActivityFeed';

const initialTasks = [
    { id: 1, status: 'completed', title: 'Call Sofia', taskType: 'Call', association: 'Sofia Sulikowski', dueDate: 'Today at 11:00 AM', lastModified: 'a day ago', isFormTask: false },
    { id: 2, status: 'pending', title: 'Complete Professional Service Request Form', taskType: 'Form', association: 'TechNet Solutions LLC', dueDate: 'Today at 5:00 PM', lastModified: '2 hours ago', isFormTask: true },
    { id: 3, status: 'pending', title: 'Follow up with Shreyas', taskType: 'Email', association: 'Shreyas', dueDate: 'Today at 3:00 PM', lastModified: '13 d ago', isFormTask: false },
    { id: 4, status: 'pending', title: 'Call Sofia', taskType: 'Call', association: 'Sofia Sulikowski', dueDate: 'Tomorrow at 8:00 AM', lastModified: 'a day ago', isFormTask: false },
    { id: 5, status: 'pending', title: 'Follow up with John', taskType: 'Email', association: 'John Doe', dueDate: 'Tomorrow at 8:00 AM', lastModified: '3 ho ago', isFormTask: false },
    { id: 6, status: 'pending', title: 'Follow up with Shelby', taskType: 'To-do', association: 'Shelby', dueDate: 'Dec 10, 2020 8:00 AM', lastModified: '21 h ago', isFormTask: false },
    { id: 7, status: 'pending', title: 'Send Quote', taskType: 'To-do', association: 'Adrienne new Qu...', dueDate: 'Dec 11, 2020 8:00 AM', lastModified: '3 d ago', isFormTask: false },
    { id: 8, status: 'pending', title: 'Follow up with John Doe', taskType: 'To-do', association: 'John Doe', dueDate: 'Dec 11, 2020 8:00 AM', lastModified: '3 ho ago', isFormTask: false },
    { id: 9, status: 'pending', title: 'Follow up with Larry', taskType: 'To-do', association: 'Larry', dueDate: 'Dec 15, 2020 8:00 AM', lastModified: 'a day ago', isFormTask: false },
];

const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length > 1 && parts[1]) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
}

const Tasks: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [tasks, setTasks] = useState(initialTasks);
    
    // Form state for creating new tasks
    const [newTask, setNewTask] = useState({
        title: '',
        taskType: 'Call',
        association: '',
        dueDate: ''
    });

    const handleTaskClick = (task: any) => {
        if (task.isFormTask) {
            navigate(`/tasks/form/${task.id}`);
        }
    };

    // Handle form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewTask(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Format datetime-local input to readable date string
    const formatDueDate = (dateTimeString: string) => {
        if (!dateTimeString) return '';
        
        const date = new Date(dateTimeString);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        const taskDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        const timeString = date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true 
        });
        
        if (taskDate.getTime() === today.getTime()) {
            return `Today at ${timeString}`;
        } else if (taskDate.getTime() === tomorrow.getTime()) {
            return `Tomorrow at ${timeString}`;
        } else {
            return `${date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
            })} ${timeString}`;
        }
    };

    // Handle form submission
    const handleCreateTask = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!newTask.title.trim() || !newTask.association.trim() || !newTask.dueDate) {
            alert('Please fill in all required fields');
            return;
        }

        const newTaskObj = {
            id: Math.max(...tasks.map(t => t.id)) + 1,
            status: 'pending' as const,
            title: newTask.title.trim(),
            taskType: newTask.taskType,
            association: newTask.association.trim(),
            dueDate: formatDueDate(newTask.dueDate),
            lastModified: 'just now',
            isFormTask: newTask.taskType === 'Form'
        };

        setTasks(prev => [newTaskObj, ...prev]);
        
        // Reset form and close modal
        setNewTask({
            title: '',
            taskType: 'Call',
            association: '',
            dueDate: ''
        });
        setShowCreateModal(false);
    };

    // Handle modal close
    const handleCloseModal = () => {
        setNewTask({
            title: '',
            taskType: 'Call',
            association: '',
            dueDate: ''
        });
        setShowCreateModal(false);
    };

    const filterTasks = () => {

        switch (activeTab) {
            case 'due-today':
                return tasks.filter(task => task.dueDate.includes('Today'));
            case 'overdue':
                return tasks.filter(task => {
                    const dueDate = task.dueDate;
                    return !dueDate.includes('Today') && !dueDate.includes('Tomorrow') && 
                           !dueDate.includes('Dec') && task.status !== 'completed';
                });
            case 'upcoming':
                return tasks.filter(task => task.dueDate.includes('Tomorrow') || task.dueDate.includes('Dec'));
            default:
                return tasks;
        }
    };

    const filteredTasks = filterTasks();
    const dueTodayCount = tasks.filter(task => task.dueDate.includes('Today')).length;
    const overdueCount = tasks.filter(task => {
        const dueDate = task.dueDate;
        return !dueDate.includes('Today') && !dueDate.includes('Tomorrow') && 
               !dueDate.includes('Dec') && task.status !== 'completed';
    }).length;
    const upcomingCount = tasks.filter(task => task.dueDate.includes('Tomorrow') || task.dueDate.includes('Dec')).length;

    return (
        <div className="w-full max-w-full overflow-hidden">
            <div className="flex flex-col xl:flex-row space-y-4 xl:space-y-0 xl:space-x-4 min-w-0">
                <div className="flex-1 min-w-0 bg-white p-6 rounded-lg shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-2xl font-semibold text-gray-800">Tasks</h1>
                        <div className="flex items-center space-x-2">
                            <button 
                                onClick={() => setShowCreateModal(true)}
                                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                            >
                                Create task
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
                            <button 
                                type="button" 
                                onClick={() => setActiveTab('all')}
                                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === 'all' 
                                        ? 'border-orange-500 text-orange-600' 
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                All ({tasks.length})
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setActiveTab('due-today')}
                                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === 'due-today' 
                                        ? 'border-orange-500 text-orange-600' 
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Due today ({dueTodayCount})
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setActiveTab('overdue')}
                                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === 'overdue' 
                                        ? 'border-orange-500 text-orange-600' 
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Overdue ({overdueCount})
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setActiveTab('upcoming')}
                                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === 'upcoming' 
                                        ? 'border-orange-500 text-orange-600' 
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Upcoming ({upcomingCount})
                            </button>
                        </nav>
                    </div>


                    {/* Task Table */}
                    <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="w-12 px-6 py-3 text-left">
                                        <input type="checkbox" className="form-checkbox h-4 w-4 text-orange-600" />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Associations</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Modified</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredTasks.map((task) => (
                                    <tr key={task.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <input type="checkbox" className="form-checkbox h-4 w-4 text-orange-600" />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {task.status === 'completed' ? (
                                                <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                            ) : (
                                                <div className="h-5 w-5 border-2 border-gray-300 rounded-full"></div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:underline cursor-pointer" onClick={() => handleTaskClick(task)}>
                                            {task.title}
                                            {/* {task.isFormTask && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                    📋 Form
                                                </span>
                                            )} */}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.taskType}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <div className="flex items-center">
                                                <div className="h-6 w-6 rounded-full bg-blue-200 flex items-center justify-center text-xs font-bold text-blue-800 mr-2">
                                                    {getInitials(task.association)}
                                                </div>
                                                {task.association}
                                            </div>
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${task.dueDate.includes('Today') || task.dueDate.includes('Tomorrow') ? 'text-red-600' : 'text-gray-500'}`}>
                                            {task.dueDate}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.lastModified}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="w-full xl:w-80 xl:max-w-80 xl:flex-shrink-0 xl:min-w-0">
                    <ActivityFeed />
                </div>
            </div>

            {/* Create Task Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Create New Task</h3>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <form onSubmit={handleCreateTask} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Task Title *</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={newTask.title}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="Enter task title"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
                                    <select 
                                        name="taskType"
                                        value={newTask.taskType}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    >
                                        <option value="Call">Call</option>
                                        <option value="Email">Email</option>
                                        <option value="To-do">To-do</option>
                                        <option value="Form">Form</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Associated Contact *</label>
                                    <input
                                        type="text"
                                        name="association"
                                        value={newTask.association}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="Enter contact name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                                    <input
                                        type="datetime-local"
                                        name="dueDate"
                                        value={newTask.dueDate}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        required
                                    />
                                </div>
                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                    >
                                        Create Task
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tasks;
